import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ScanFace,
  Shield,
  KeyRound,
  Sparkles,
  Timer,
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Info,
  Activity,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { useThemeStore } from '../../stores/theme.store';
import { api } from '../../lib/api';
import {
  TenantFacialConfig,
  UpdateFacialConfigDto,
  useFacialConfig,
  useUpdateFacialConfig,
  useRotateFacialKey,
} from '../../hooks/useFacialConfig';

// ============================================================
// Types — local form state mirrors server shape
// ============================================================

interface FormState {
  // Operations
  requireOnLogin: boolean;
  requireOnPixAboveValue: boolean;
  requireOnWithdrawal: boolean;
  requireOnTransfer: boolean;
  requireOnProfileChange: boolean;

  // Threshold (displayed in R$, stored in centavos)
  pixValueThresholdReais: string;

  // Advanced features
  livenessEnabled: boolean;
  deepfakeAnalysisEnabled: boolean;
  mapping3DEnabled: boolean;
  continuousMonitorEnabled: boolean;
  documentMatchEnabled: boolean;

  // Bounds
  continuousIntervalMinutes: number;
  maxVerificationAttempts: number;
  verifiedSessionMinutes: number;
}

interface FacialMetrics {
  totalVerifications: number;
  successCount: number;
  failedCount: number;
  spoofingCount: number;
  deepfakeCount: number;
  timeoutCount: number;
  averageDurationMs: number;
  successRate: number;
}

// ============================================================
// Helpers — centavos <-> reais
// ============================================================

function centsToReais(cents: number | string | bigint | null | undefined): string {
  if (cents === null || cents === undefined) return '0,00';
  const n = typeof cents === 'string' ? Number(cents) : Number(cents);
  if (!Number.isFinite(n)) return '0,00';
  return (n / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function reaisToCents(reais: string): number {
  if (!reais) return 0;
  const normalized = reais.trim().replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function configToForm(config: TenantFacialConfig): FormState {
  return {
    requireOnLogin: config.requireOnLogin,
    requireOnPixAboveValue: config.requireOnPixAboveValue,
    requireOnWithdrawal: config.requireOnWithdrawal,
    requireOnTransfer: config.requireOnTransfer,
    requireOnProfileChange: config.requireOnProfileChange,

    pixValueThresholdReais: centsToReais(config.pixValueThreshold),

    livenessEnabled: config.livenessEnabled,
    deepfakeAnalysisEnabled: config.deepfakeAnalysisEnabled,
    mapping3DEnabled: config.mapping3DEnabled,
    continuousMonitorEnabled: config.continuousMonitorEnabled,
    documentMatchEnabled: config.documentMatchEnabled,

    continuousIntervalMinutes: config.continuousIntervalMinutes,
    maxVerificationAttempts: config.maxVerificationAttempts,
    verifiedSessionMinutes: config.verifiedSessionMinutes,
  };
}

function formToPayload(form: FormState): UpdateFacialConfigDto {
  return {
    requireOnLogin: form.requireOnLogin,
    requireOnPixAboveValue: form.requireOnPixAboveValue,
    requireOnWithdrawal: form.requireOnWithdrawal,
    requireOnTransfer: form.requireOnTransfer,
    requireOnProfileChange: form.requireOnProfileChange,

    pixValueThreshold: reaisToCents(form.pixValueThresholdReais),

    livenessEnabled: form.livenessEnabled,
    deepfakeAnalysisEnabled: form.deepfakeAnalysisEnabled,
    mapping3DEnabled: form.mapping3DEnabled,
    continuousMonitorEnabled: form.continuousMonitorEnabled,
    documentMatchEnabled: form.documentMatchEnabled,

    continuousIntervalMinutes: clamp(form.continuousIntervalMinutes, 2, 30),
    maxVerificationAttempts: clamp(form.maxVerificationAttempts, 2, 10),
    verifiedSessionMinutes: clamp(form.verifiedSessionMinutes, 5, 60),
  };
}

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(Math.floor(n), min), max);
}

// ============================================================
// Reusable primitives (dark-mode aware)
// ============================================================

interface FieldRowProps {
  label: string;
  description?: string;
  children: React.ReactNode;
}

function FieldRow({ label, description, children }: FieldRowProps) {
  const { isDarkMode } = useThemeStore();
  const labelClass = isDarkMode ? 'text-surface-200' : 'text-gray-900';
  const descClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  return (
    <div className="flex items-center justify-between gap-6 py-3">
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${labelClass}`}>{label}</div>
        {description && <div className={`text-xs mt-0.5 ${descClass}`}>{description}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

interface SectionCardProps {
  title: string;
  description?: string;
  icon: React.ElementType;
  children: React.ReactNode;
}

function SectionCard({ title, description, icon: Icon, children }: SectionCardProps) {
  const { isDarkMode } = useThemeStore();
  const titleClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const descClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  const dividerClass = isDarkMode ? 'divide-surface-800' : 'divide-gray-200';
  return (
    <Card className="p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className={`text-lg font-semibold ${titleClass}`}>{title}</h2>
          {description && <p className={`text-sm mt-0.5 ${descClass}`}>{description}</p>}
        </div>
      </div>
      <div className={`divide-y ${dividerClass}`}>{children}</div>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-10 w-40" />
      </div>
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="p-6 space-y-4">
          <Skeleton className="h-6 w-48" />
          {[1, 2, 3].map((j) => (
            <div key={j} className="flex items-center justify-between py-2">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

// ============================================================
// Metrics Section — fetched via direct api.get
// ============================================================

interface MetricsSectionProps {
  isDarkMode: boolean;
}

function MetricsSection({ isDarkMode }: MetricsSectionProps) {
  const [metrics, setMetrics] = useState<FacialMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<FacialMetrics>('/facial-verification-advanced/audit/metrics')
      .then((res) => {
        if (!cancelled) {
          setMetrics(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Erro ao carregar métricas';
          setError(String(msg));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const titleClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const descClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  const valueClass = isDarkMode ? 'text-surface-50' : 'text-gray-900';

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400">
            <Activity className="w-5 h-5" />
          </div>
          <div>
            <h2 className={`text-lg font-semibold ${titleClass}`}>Métricas de Verificação</h2>
            <p className={`text-sm mt-0.5 ${descClass}`}>
              Desempenho e tentativas de fraude das últimas verificações faciais.
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-sm text-amber-400">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      ) : metrics ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricTile
            label="Verificações totais"
            value={metrics.totalVerifications.toLocaleString('pt-BR')}
            sub={`${metrics.successCount} aprovadas · ${metrics.failedCount} rejeitadas`}
            isDarkMode={isDarkMode}
          />
          <MetricTile
            label="Taxa de sucesso"
            value={`${(metrics.successRate * 100).toFixed(1)}%`}
            sub="Aprovações sobre total"
            tone="success"
            isDarkMode={isDarkMode}
          />
          <MetricTile
            label="Tentativas de spoofing"
            value={(metrics.spoofingCount ?? 0).toLocaleString('pt-BR')}
            sub={`Deepfakes: ${(metrics.deepfakeCount ?? 0).toLocaleString('pt-BR')}`}
            tone={(metrics.spoofingCount ?? 0) > 0 ? 'danger' : 'default'}
            isDarkMode={isDarkMode}
          />
          <MetricTile
            label="Tempo médio"
            value={`${metrics.averageDurationMs.toFixed(0)} ms`}
            sub="Por verificação"
            isDarkMode={isDarkMode}
          />
        </div>
      ) : (
        <div className={`text-sm ${descClass}`}>Nenhuma métrica disponível.</div>
      )}

      {metrics && (
        <p className={`text-xs mt-4 ${descClass}`}>
          <span className={valueClass}>Falhas:</span>{' '}
          {(metrics.failedCount ?? 0).toLocaleString('pt-BR')}{' · '}
          <span className={valueClass}>Timeouts:</span>{' '}
          {(metrics.timeoutCount ?? 0).toLocaleString('pt-BR')}
        </p>
      )}
    </Card>
  );
}

interface MetricTileProps {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'success' | 'danger';
  isDarkMode: boolean;
}

function MetricTile({ label, value, sub, tone = 'default', isDarkMode }: MetricTileProps) {
  const labelClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  const subClass = isDarkMode ? 'text-surface-500' : 'text-gray-500';
  const valueColor =
    tone === 'success'
      ? 'text-emerald-400'
      : tone === 'danger'
        ? 'text-red-400'
        : isDarkMode
          ? 'text-surface-50'
          : 'text-gray-900';
  const borderColor =
    tone === 'success'
      ? 'border-emerald-500/30'
      : tone === 'danger'
        ? 'border-red-500/30'
        : isDarkMode
          ? 'border-surface-800'
          : 'border-gray-200';

  return (
    <div className={`rounded-lg border p-4 ${borderColor}`}>
      <div className={`text-xs uppercase tracking-wide ${labelClass}`}>{label}</div>
      <div className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${subClass}`}>{sub}</div>}
    </div>
  );
}

// ============================================================
// Main page
// ============================================================

export function FacialConfigPage() {
  const { isDarkMode } = useThemeStore();
  const { data: config, isLoading, error } = useFacialConfig();
  const updateMutation = useUpdateFacialConfig();
  const rotateMutation = useRotateFacialKey();

  const [form, setForm] = useState<FormState | null>(null);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (config) setForm(configToForm(config));
  }, [config]);

  const hasChanges = useMemo(() => {
    if (!config || !form) return false;
    const serverForm = configToForm(config);
    return JSON.stringify(serverForm) !== JSON.stringify(form);
  }, [config, form]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      await updateMutation.mutateAsync(formToPayload(form));
      showToast('success', 'Configuração facial salva com sucesso');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar configuração';
      showToast('error', msg);
    }
  };

  const handleDiscard = () => {
    if (config) setForm(configToForm(config));
  };

  const handleRotateKey = async () => {
    try {
      const result = await rotateMutation.mutateAsync();
      setShowRotateModal(false);
      showToast(
        'success',
        `Master key rotacionada com sucesso. Nova versão: ${result.newVersion}`,
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao rotacionar master key';
      showToast('error', msg);
    }
  };

  const titleClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const subtitleClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';

  if (isLoading || !form) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className={`text-lg font-medium ${titleClass} mb-2`}>Erro ao carregar</h3>
          <p className={subtitleClass}>
            Não foi possível carregar a configuração de verificação facial.
          </p>
        </Card>
      </div>
    );
  }

  const ActionButtons = (
    <div className="flex items-center gap-2">
      {hasChanges && (
        <Button variant="ghost" size="sm" onClick={handleDiscard}>
          Descartar
        </Button>
      )}
      <Button
        variant="primary"
        size="sm"
        onClick={handleSave}
        disabled={!hasChanges || updateMutation.isPending}
        isLoading={updateMutation.isPending}
        className="flex items-center gap-2"
      >
        <Save className="w-4 h-4" /> Salvar
      </Button>
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{toast.message}</span>
        </motion.div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${titleClass} flex items-center gap-3`}>
            <ScanFace className="w-7 h-7 text-primary-400" />
            Verificação Facial Avançada
          </h1>
          <p className={`${subtitleClass} mt-1`}>
            Configure quando a verificação facial é exigida e quais recursos estão ativos.
          </p>
        </div>
        {ActionButtons}
      </div>

      {/* Version info */}
      <Card className="p-4 border-primary-500/30">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-primary-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className={`text-sm ${subtitleClass}`}>
              Master key versão{' '}
              <span className={`font-semibold ${titleClass}`}>
                v{config.masterKeyVersion}
              </span>
              {' · '}
              Última atualização{' '}
              <span className={titleClass}>
                {new Date(config.updatedAt).toLocaleString('pt-BR')}
              </span>
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRotateModal(true)}
            className="flex items-center gap-2"
          >
            <KeyRound className="w-4 h-4" /> Rotacionar master key
          </Button>
        </div>
      </Card>

      {/* Warning banner */}
      <Card className="p-4 border-amber-500/40 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <h3 className={`font-semibold ${titleClass}`}>Atenção</h3>
            <p className={`text-sm ${subtitleClass} mt-0.5`}>
              As alterações abaixo afetam todos os correntistas do tenant e operações de alto
              valor. Valide em ambiente de homologação antes de ativar.
            </p>
          </div>
        </div>
      </Card>

      {/* Unsaved changes banner (top) */}
      {hasChanges && (
        <Card className="p-3 border-primary-500/40 bg-primary-500/5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className={`text-sm ${titleClass}`}>Alterações não salvas</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleDiscard}>
                Descartar
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                isLoading={updateMutation.isPending}
              >
                Salvar
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 1. Operações que exigem verificação */}
      <SectionCard
        title="Operações protegidas"
        description="Defina em quais operações a verificação facial é obrigatória."
        icon={Shield}
      >
        <FieldRow
          label="Exigir no login"
          description="Solicita verificação facial ao fazer login no internet banking."
        >
          <Switch
            checked={form.requireOnLogin}
            onCheckedChange={(v) => set('requireOnLogin', v)}
          />
        </FieldRow>
        <FieldRow
          label="Exigir em PIX acima do limite"
          description="Solicita verificação facial em PIX acima do valor configurado abaixo."
        >
          <Switch
            checked={form.requireOnPixAboveValue}
            onCheckedChange={(v) => set('requireOnPixAboveValue', v)}
          />
        </FieldRow>
        <FieldRow
          label="Valor limite para PIX"
          description="PIX com valor acima deste limite exige verificação facial."
        >
          <div className="w-40">
            <Input
              leftIcon={<span className="text-surface-400 text-sm">R$</span>}
              inputMode="decimal"
              value={form.pixValueThresholdReais}
              onChange={(e) => set('pixValueThresholdReais', e.target.value)}
              disabled={!form.requireOnPixAboveValue}
              placeholder="0,00"
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Exigir em saques"
          description="Solicita verificação facial em operações de saque."
        >
          <Switch
            checked={form.requireOnWithdrawal}
            onCheckedChange={(v) => set('requireOnWithdrawal', v)}
          />
        </FieldRow>
        <FieldRow
          label="Exigir em transferências"
          description="Solicita verificação facial em transferências entre contas."
        >
          <Switch
            checked={form.requireOnTransfer}
            onCheckedChange={(v) => set('requireOnTransfer', v)}
          />
        </FieldRow>
        <FieldRow
          label="Exigir em alteração de perfil"
          description="Solicita verificação facial ao alterar dados sensíveis do perfil."
        >
          <Switch
            checked={form.requireOnProfileChange}
            onCheckedChange={(v) => set('requireOnProfileChange', v)}
          />
        </FieldRow>
      </SectionCard>

      {/* 2. Recursos avançados */}
      <SectionCard
        title="Recursos avançados"
        description="Ative as camadas anti-fraude e biometria avançada."
        icon={Sparkles}
      >
        <FieldRow
          label="Detecção de liveness"
          description="Aplica desafios passivos e ativos para confirmar que há uma pessoa real."
        >
          <Switch
            checked={form.livenessEnabled}
            onCheckedChange={(v) => set('livenessEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Análise anti-deepfake"
          description="Rejeita vídeos e imagens sintéticas geradas por IA."
        >
          <Switch
            checked={form.deepfakeAnalysisEnabled}
            onCheckedChange={(v) => set('deepfakeAnalysisEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Mapeamento 3D"
          description="Valida profundidade do rosto para evitar ataques de foto e máscaras."
        >
          <Switch
            checked={form.mapping3DEnabled}
            onCheckedChange={(v) => set('mapping3DEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Monitoramento contínuo"
          description="Re-autentica o usuário silenciosamente durante a sessão ativa."
        >
          <Switch
            checked={form.continuousMonitorEnabled}
            onCheckedChange={(v) => set('continuousMonitorEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Comparação com documento"
          description="Compara o rosto vivo com a foto do documento do KYC."
        >
          <Switch
            checked={form.documentMatchEnabled}
            onCheckedChange={(v) => set('documentMatchEnabled', v)}
          />
        </FieldRow>
      </SectionCard>

      {/* 3. Limites e janelas */}
      <SectionCard
        title="Limites e janelas"
        description="Ajuste os parâmetros de sessão, monitoramento e tentativas."
        icon={Timer}
      >
        <FieldRow
          label="Intervalo do monitoramento contínuo (minutos)"
          description="Janela entre capturas silenciosas. Valor entre 2 e 30 minutos."
        >
          <div className="w-32">
            <Input
              type="number"
              min={2}
              max={30}
              value={form.continuousIntervalMinutes}
              onChange={(e) =>
                set('continuousIntervalMinutes', Number(e.target.value))
              }
              disabled={!form.continuousMonitorEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Máximo de tentativas de verificação"
          description="Tentativas permitidas antes de bloqueio temporário. Entre 2 e 10."
        >
          <div className="w-32">
            <Input
              type="number"
              min={2}
              max={10}
              value={form.maxVerificationAttempts}
              onChange={(e) =>
                set('maxVerificationAttempts', Number(e.target.value))
              }
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Duração da sessão verificada (minutos)"
          description="Tempo em que o token de verificação é aceito sem nova biometria. Entre 5 e 60."
        >
          <div className="w-32">
            <Input
              type="number"
              min={5}
              max={60}
              value={form.verifiedSessionMinutes}
              onChange={(e) => set('verifiedSessionMinutes', Number(e.target.value))}
            />
          </div>
        </FieldRow>
      </SectionCard>

      {/* 4. Métricas */}
      <MetricsSection isDarkMode={isDarkMode} />

      {/* Info footer */}
      <Card className="p-4 border-primary-500/30">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-primary-400 mt-0.5 shrink-0" />
          <p className={`text-sm ${subtitleClass}`}>
            Os embeddings faciais são criptografados com AES-256-GCM e chave derivada por
            usuário (HKDF). Nenhuma imagem bruta é armazenada. A rotação da master key re-
            criptografa todos os embeddings do tenant de forma assíncrona.
          </p>
        </div>
      </Card>

      {/* Bottom actions */}
      <div className="flex justify-end">{ActionButtons}</div>

      {/* Floating banner when there are changes */}
      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40"
        >
          <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-surface-800 border border-primary-500/40 shadow-lg">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-sm text-surface-200">Alterações não salvas</span>
            <Button variant="ghost" size="sm" onClick={handleDiscard}>
              Descartar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              isLoading={updateMutation.isPending}
            >
              Salvar
            </Button>
          </div>
        </motion.div>
      )}

      {/* Rotate master key confirmation modal */}
      <Modal
        isOpen={showRotateModal}
        onClose={() => setShowRotateModal(false)}
        title="Rotacionar master key"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-500/40 bg-amber-500/5">
            <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-surface-300">
              Esta operação gera uma nova master key e re-criptografa todos os embeddings
              faciais do tenant. O processo é executado em background e pode levar alguns
              minutos. Verificações faciais continuam funcionando normalmente durante a
              rotação.
            </p>
          </div>
          <p className="text-surface-300 text-sm">
            Versão atual:{' '}
            <span className="font-semibold text-surface-100">
              v{config.masterKeyVersion}
            </span>{' '}
            → próxima versão:{' '}
            <span className="font-semibold text-primary-400">
              v{config.masterKeyVersion + 1}
            </span>
          </p>
          <p className="text-surface-300 text-sm">
            Tem certeza que deseja rotacionar a master key agora?
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowRotateModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleRotateKey}
              isLoading={rotateMutation.isPending}
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Rotacionar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default FacialConfigPage;
